import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../lib/supabase';

const { FiShield, FiLock, FiMail, FiLogIn } = FiIcons;

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        navigate('/admin/dashboard');
      }
    };

    checkAuth();
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        setError(error.message);
      } else if (data.user) {
        navigate('/admin/dashboard');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="clay-card"
      >
        <div className="flex items-center space-x-4 mb-8">
          <div className="clay-button p-4">
            <SafeIcon icon={FiShield} className="text-2xl text-text" />
          </div>
          <h1 className="text-3xl font-bold text-text">Admin Login</h1>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-lg font-bold text-heading mb-3">
              Email
            </label>
            <div className="relative">
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                className="clay-input w-full pl-12 pr-4 py-4 text-text placeholder-gray-500 font-medium focus-visible"
              />
              <SafeIcon 
                icon={FiMail} 
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-xl text-text" 
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-lg font-bold text-heading mb-3">
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                className="clay-input w-full pl-12 pr-4 py-4 text-text placeholder-gray-500 font-medium focus-visible"
              />
              <SafeIcon 
                icon={FiLock} 
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-xl text-text" 
              />
            </div>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="clay-card bg-pink p-4 border-l-4 border-red-400"
            >
              <p className="text-red-700 font-bold">{error}</p>
            </motion.div>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`clay-button-purple w-full py-4 flex items-center justify-center space-x-3 font-bold transition-all duration-300 focus-visible ${
              loading ? 'opacity-50 cursor-not-allowed' : 'hover:transform hover:scale-105'
            }`}
          >
            <SafeIcon icon={FiLogIn} className="text-xl text-text" />
            <span className="text-lg">{loading ? 'Logging in...' : 'Login'}</span>
          </button>
        </form>

        <div className="mt-8 clay-card bg-yellow p-4">
          <p className="text-text text-center font-medium">
            This area is restricted to administrators only.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLogin;